import { HighwaySectionDto, CityDto, ZoneDto } from '@interfaces/types';
import { DayOfWeek } from '@interfaces/models';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

export const useServicesFormatter = () => {
  const { t } = useTranslation();

  const formatCities = (cityCodes: string[], cities: CityDto[]) => {
    const allCitiesLength = cities.length;

    return cityCodes.length === allCitiesLength
      ? t('COMPANY_POLICY.ALL_CITIES', {
          count: allCitiesLength,
        })
      : cityCodes.length > 3
        ? cityCodes
            .slice(0, 3)
            .map((cityCode) => cities.find((city) => city.code === cityCode)?.name)
            .join(', ') + ` (+${cityCodes.length - 3})`
        : cityCodes
            .map((cityCode) => cities.find((city) => city.code === cityCode)?.name)
            .join(', ');
  };

  const formatDays = (days: DayOfWeek[]) => {
    const wholeWeek = days.length === 7;

    const isOnlyWeekend =
      days.length === 2 && days.includes(DayOfWeek.SATURDAY) && days.includes(DayOfWeek.SUNDAY);

    const isOnlyWorkingDays =
      days.length === 5 &&
      days.includes(DayOfWeek.MONDAY) &&
      days.includes(DayOfWeek.TUESDAY) &&
      days.includes(DayOfWeek.WEDNESDAY) &&
      days.includes(DayOfWeek.THURSDAY) &&
      days.includes(DayOfWeek.FRIDAY);

    return wholeWeek
      ? t('COMPANY_POLICY.ALL_DAYS')
      : isOnlyWeekend
        ? t('COMPANY_POLICY.WEEKEND')
        : isOnlyWorkingDays
          ? t('COMPANY_POLICY.WORKING_DAYS')
          : days.map((day) => t(`DAYS_SHORT.${day}`)).join(', ');
  };

  const formatTime = (unlimitedTime: boolean, timeFrom: string, timeTo: string) => {
    return unlimitedTime ? t('COMPANY_POLICY.UNLIMITED_TIME') : `${timeFrom}:00 - ${timeTo}:00`;
  };

  const formatHighwaysSections = (
    highwaySections: string[],
    highwaysSections: HighwaySectionDto[],
  ): string => {
    if (highwaySections.length === highwaysSections.length) {
      return t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_ALL_SECTIONS', {
        count: highwaysSections.length,
      });
    }

    return highwaySections
      .map((sectionCode) => {
        const section = highwaysSections.find((section) => section.code === sectionCode);
        return `${section?.highwayName}: ${t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SECTION')} ${section?.sectionName}`;
      })
      .join(', ');
  };

  const allZonesLabel = useCallback(
    (allZonesSelectedIn: number, max: number) => {
      if (allZonesSelectedIn === 0) return [];
      if (allZonesSelectedIn === max) {
        return [t('COMPANY_POLICY.ZONES_ALL')];
      }

      return [
        t('COMPANY_POLICY.ZONES_ALL_SELECTED', {
          count: allZonesSelectedIn,
          label: t('COMPANY_POLICY.ZONES_CITY_LABEL', {
            postProcess: 'interval',
            count: allZonesSelectedIn,
          }),
        }),
      ];
    },
    [t],
  );

  const formatZones = (
    selectedCityCodes: string[],
    selectedZoneCodes: string[],
    cities: CityDto[],
    zones: ZoneDto[],
  ) => {
    const selectedCities = cities.filter(({ code }) => selectedCityCodes.includes(code));
    const citiesSelectedCount = selectedCities.map(({ code, name }) => ({
      code,
      name,
      selected: zones.filter(
        (zone) => zone.cityCode === code && selectedZoneCodes.includes(zone.code),
      ).length,
      max: zones.filter((zone) => zone.cityCode === code).length,
    }));

    const allZonesSelected = citiesSelectedCount.filter(({ selected, max }) => selected === max);
    const notAllZoneSelected = citiesSelectedCount.filter(({ selected, max }) => selected !== max);

    return [
      ...allZonesLabel(allZonesSelected.length, selectedCities.length),
      ...notAllZoneSelected.map(({ name, selected, max }) => `${name} (${selected}/${max})`),
    ].join(', ');
  };

  return { formatCities, formatDays, formatTime, formatHighwaysSections, formatZones };
};
